import React, { useEffect } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';

import { Stack } from '@mui/material';

import { useAppDispatch, useAppSelector } from 'state/hooks';
import { useGetProjectQuery } from 'state/projectSlices';
import {
  ProjectFeatures,
  kUserStatusLoggedIn,
  selectUserState,
} from 'state/userSlice';
import {
  selectCurrentProjectInfo,
  selectProject,
  setProjectSlug,
  setStateFromSearchParams,
} from 'state/workflowSlice';

import { useAppDispatchUserStatus } from 'app/AppDispatcher';
import { NotificationsAppHeader } from 'features/app_header/AppHeader';
import { classNames } from 'features/common/utils';

import { NotificationsSidebar } from './NotificationsSidebar';
import { parseNotificationsPath } from './alertHelpers';

import styles from './NotificationsPanel.module.css';

export interface NotificationsPanelProps extends React.PropsWithChildren {
  childrenWrapperClass?: string;
}

export function NotificationsPanel({
  children,
  childrenWrapperClass,
}: NotificationsPanelProps) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const userInfo = useAppSelector(selectUserState);
  const projectSlug = useAppSelector(selectProject);
  const projectInfo = useAppSelector(selectCurrentProjectInfo);
  useAppDispatchUserStatus();

  const { data: project } = useGetProjectQuery(projectSlug!, {
    skip: !projectSlug,
  });

  useEffect(() => {
    if (location.search) {
      dispatch(setStateFromSearchParams(location.search));
    }
  }, [dispatch, location.search]);

  useEffect(() => {
    // Sync projectSlug in redux with the URL
    // Notifications is reading the projectSlug from the URL as opposed to rest of the app which is reading from the store
    const parsed = parseNotificationsPath(location.pathname);
    if (!parsed) return;
    if (parsed.projectSlug !== projectSlug) {
      dispatch(setProjectSlug(parsed.projectSlug));
    }
  }, [location.pathname, projectSlug, dispatch]);

  // if user is not logged in
  if (
    userInfo &&
    userInfo.status !== 'unknown' &&
    userInfo.status !== kUserStatusLoggedIn
  ) {
    return <Navigate to="/" replace />;
  }

  // if a project doesn't have Alert feature, do not allow user to access notifications page
  if (projectInfo && !projectInfo.features?.includes(ProjectFeatures.ALERTS)) {
    return <Navigate to="/" replace />;
  }

  // if project doesn't have alerts_enabled, do not allow user with no sub admin access to access notifications page
  if (
    projectInfo &&
    !projectInfo.is_sub_admin &&
    project &&
    !project.are_alerts_enabled
  ) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="App">
      <NotificationsAppHeader close={() => navigate('/app')} />
      <div className="App-body">
        <Stack className={styles.notificationsPanelContainer}>
          <Stack
            direction="row"
            className={styles.notificationsPanelSubcontainer}
          >
            <NotificationsSidebar />
            {children && (
              <Stack className={styles.fullWidthContentContainerOuter}>
                <Stack
                  className={classNames(
                    childrenWrapperClass || styles.contentBlock,
                  )}
                >
                  {children}
                </Stack>
              </Stack>
            )}
          </Stack>
        </Stack>
      </div>
    </div>
  );
}
